"use strict";
import { createFont } from "@tamagui/core";
import { needsSmallFont } from "ui/src/utils/needs-small-font";
const adjustedSize = (fontSize) => {
  if (needsSmallFont()) {
    return fontSize;
  }
  return fontSize + 1;
};
const Roboto = 'Roboto, -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
const Inter = 'Inter, -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
const NunitoSans = '"Nunito Sans", Inter, -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
const Sora = 'Sora, "Nunito Sans", -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
const JetBrainsMono = '"JetBrains Mono", "Roboto Mono", ui-monospace, SFMono-Regular, Menlo, monospace';
const REGULAR_WEIGHT = "485";
const MEDIUM_WEIGHT = "535";
const SEMIBOLD_WEIGHT = "600";
const BOLD_WEIGHT = "700";
const defaultWeights = {
  normal: REGULAR_WEIGHT,
  true: REGULAR_WEIGHT,
  medium: MEDIUM_WEIGHT,
  semibold: SEMIBOLD_WEIGHT,
  bold: BOLD_WEIGHT
};
export const NATIVE_LINE_HEIGHT_SCALE = 1.15;
export const fonts = {
  heading1: {
    family: "Sora",
    fontSize: adjustedSize(72),
    lineHeight: adjustedSize(72) * 0.96,
    fontWeight: SEMIBOLD_WEIGHT,
    maxFontSizeMultiplier: 1.2,
    letterSpacing: "-2%"
  },
  heading2: {
    family: "Sora",
    fontSize: adjustedSize(48),
    lineHeight: adjustedSize(48) * 1.08,
    fontWeight: SEMIBOLD_WEIGHT,
    maxFontSizeMultiplier: 1.2,
    letterSpacing: "-1%"
  },
  heading3: {
    family: "Sora",
    fontSize: adjustedSize(32),
    lineHeight: adjustedSize(32) * 1.2,
    fontWeight: MEDIUM_WEIGHT,
    maxFontSizeMultiplier: 1.2,
    letterSpacing: "-0.5%"
  },
  subheading1: {
    family: "Sora",
    fontSize: adjustedSize(20),
    lineHeight: 28,
    fontWeight: MEDIUM_WEIGHT,
    maxFontSizeMultiplier: 1.4
  },
  subheading2: {
    family: "Sora",
    fontSize: adjustedSize(16),
    lineHeight: 20,
    fontWeight: MEDIUM_WEIGHT,
    maxFontSizeMultiplier: 1.4
  },
  body1: {
    family: "Nunito Sans",
    fontSize: adjustedSize(20),
    lineHeight: adjustedSize(20) * 1.3,
    fontWeight: REGULAR_WEIGHT,
    maxFontSizeMultiplier: 1.4,
    letterSpacing: "-0.09em",
    wordSpacing: "-0.09em"
  },
  body2: {
    family: "Nunito Sans",
    fontSize: adjustedSize(16),
    lineHeight: adjustedSize(16) * 1.3,
    fontWeight: REGULAR_WEIGHT,
    maxFontSizeMultiplier: 1.4,
    letterSpacing: "-0.09em",
    wordSpacing: "-0.09em"
  },
  body3: {
    family: "Nunito Sans",
    fontSize: adjustedSize(14),
    lineHeight: adjustedSize(14) * 1.3,
    fontWeight: REGULAR_WEIGHT,
    maxFontSizeMultiplier: 1.4,
    letterSpacing: "-0.09em",
    wordSpacing: "-0.09em"
  },
  body4: {
    family: "Nunito Sans",
    fontSize: adjustedSize(12),
    lineHeight: 16,
    fontWeight: REGULAR_WEIGHT,
    maxFontSizeMultiplier: 1.4,
    letterSpacing: "-0.09em",
    wordSpacing: "-0.09em"
  },
  buttonLabel1: {
    family: "Nunito Sans",
    // medium
    fontSize: adjustedSize(18),
    lineHeight: adjustedSize(18) * NATIVE_LINE_HEIGHT_SCALE,
    fontWeight: MEDIUM_WEIGHT,
    maxFontSizeMultiplier: 1.2
  },
  buttonLabel2: {
    family: "Nunito Sans",
    // medium
    fontSize: adjustedSize(16),
    lineHeight: adjustedSize(16) * NATIVE_LINE_HEIGHT_SCALE,
    fontWeight: MEDIUM_WEIGHT,
    maxFontSizeMultiplier: 1.2
  },
  buttonLabel3: {
    family: "Nunito Sans",
    // medium
    fontSize: adjustedSize(14),
    lineHeight: adjustedSize(14) * NATIVE_LINE_HEIGHT_SCALE,
    fontWeight: MEDIUM_WEIGHT,
    maxFontSizeMultiplier: 1.2
  },
  buttonLabel4: {
    family: "Nunito Sans",
    // medium
    fontSize: adjustedSize(12),
    lineHeight: adjustedSize(12) * NATIVE_LINE_HEIGHT_SCALE,
    fontWeight: MEDIUM_WEIGHT,
    maxFontSizeMultiplier: 1.2
  },
  monospace: {
    family: "JetBrains Mono",
    fontSize: adjustedSize(12),
    lineHeight: 16,
    maxFontSizeMultiplier: 1.2
  }
};
export const headingFont = createFont({
  family: Sora,
  size: {
    small: fonts.heading3.fontSize,
    medium: fonts.heading2.fontSize,
    true: fonts.heading2.fontSize,
    large: fonts.heading1.fontSize
  },
  weight: defaultWeights,
  lineHeight: {
    small: fonts.heading3.lineHeight,
    medium: fonts.heading2.lineHeight,
    true: fonts.heading2.lineHeight,
    large: fonts.heading1.lineHeight
  }
});
export const subHeadingFont = createFont({
  family: Sora,
  size: {
    small: fonts.subheading2.fontSize,
    large: fonts.subheading1.fontSize,
    true: fonts.subheading1.fontSize
  },
  weight: defaultWeights,
  lineHeight: {
    small: fonts.subheading2.lineHeight,
    large: fonts.subheading1.lineHeight,
    true: fonts.subheading1.lineHeight
  }
});
export const bodyFont = createFont({
  family: NunitoSans,
  size: {
    micro: fonts.body4.fontSize,
    small: fonts.body3.fontSize,
    medium: fonts.body2.fontSize,
    true: fonts.body2.fontSize,
    large: fonts.body1.fontSize
  },
  weight: defaultWeights,
  lineHeight: {
    micro: fonts.body4.lineHeight,
    small: fonts.body3.lineHeight,
    medium: fonts.body2.lineHeight,
    true: fonts.body2.lineHeight,
    large: fonts.body1.lineHeight
  },
  letterSpacing: {
    tight: -1
  },
  wordSpacing: {
    tight: -1
  }
});
export const buttonFont = createFont({
  family: NunitoSans,
  size: {
    micro: fonts.buttonLabel4.fontSize,
    small: fonts.buttonLabel3.fontSize,
    medium: fonts.buttonLabel2.fontSize,
    large: fonts.buttonLabel1.fontSize,
    true: fonts.buttonLabel2.fontSize
  },
  weight: {
    ...defaultWeights,
    true: MEDIUM_WEIGHT
  },
  lineHeight: {
    micro: fonts.buttonLabel4.lineHeight,
    small: fonts.buttonLabel3.lineHeight,
    medium: fonts.buttonLabel2.lineHeight,
    large: fonts.buttonLabel1.lineHeight,
    true: fonts.buttonLabel2.lineHeight
  }
});
export const allFonts = {
  heading: headingFont,
  subHeading: subHeadingFont,
  body: bodyFont,
  button: buttonFont
};
